import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortalHolding } from 'views/MyCellar/hooks/usePortalHoldings';
import { SUB_TABS_ENUMS } from 'views/Trade/types';
import { useTheme } from '../../../../context/ThemeContext';
import { NavigationPath } from '../../../../types/DomainTypes';

interface PortfolioQuickStatsProps {
  holdings: PortalHolding[];
  isLoading?: boolean;
}

interface HealthStat {
  label: string;
  value: string;
  numericValue: number;
  max: number;
  min: number;
  iconBg: string;
  iconBgDark: string;
  valueColor: string;
  valueColorDark: string;
  barGradient: string;
  barGradientDark: string;
  icon: JSX.Element;
  tooltip: string;
}

interface TradingStat {
  label: string;
  value: string;
  valueColor: string;
  valueColorDark: string;
  ctaText: string;
  ctaColor: string;
  ctaColorDark: string;
  onClick: () => void;
  icon: JSX.Element;
}

const HealthStatCard: FC<{ stat: HealthStat; pct: number; isDark: boolean }> = ({ stat, pct, isDark }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="rounded-xl p-4 transition-all relative"
      style={{
        backgroundColor: isDark ? '#0F1E32' : '#FFFFFF',
        border: `1px solid ${isDark ? '#1E3A50' : '#E2E8F0'}`,
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: isDark ? stat.iconBgDark : stat.iconBg }}
        >
          {stat.icon}
        </div>
        <span
          className="text-base sm:text-lg font-bold"
          style={{ color: isDark ? stat.valueColorDark : stat.valueColor }}
        >
          {stat.value}
        </span>
      </div>
      {/* Gauge bar */}
      <div
        className="w-full h-[5px] rounded-full mb-2 overflow-hidden"
        style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: isDark ? stat.barGradientDark : stat.barGradient,
          }}
        />
      </div>
      <div className="text-[11px] font-medium" style={{ color: isDark ? '#8B9CB3' : '#64748B' }}>
        {stat.label}
      </div>
      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute left-1/2 transform -translate-x-1/2 px-4 py-3 rounded-lg shadow-lg text-sm pointer-events-none z-20 w-56"
          style={{
            bottom: '100%',
            marginBottom: '8px',
            backgroundColor: isDark ? '#1A2E44' : '#FFFFFF',
            border: `1px solid ${isDark ? '#1E3A50' : '#E2E8F0'}`,
            color: isDark ? '#E2E8F0' : '#334155',
            whiteSpace: 'pre-line',
          }}
        >
          {stat.tooltip}
        </div>
      )}
    </div>
  );
};

const PortfolioQuickStats: FC<PortfolioQuickStatsProps> = ({ holdings, isLoading }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const { healthStats, tradingStats } = useMemo(() => {
    const withGrade = holdings.filter((h) => h.grade !== null && h.grade !== undefined);
    const withFV = holdings.filter((h) => h.fairValueScore !== null && h.fairValueScore !== undefined);
    const withLiq = holdings.filter((h) => h.liquidityScore !== null && h.liquidityScore !== undefined);

    const avgGradeNum =
      withGrade.length > 0 ? withGrade.reduce((sum, h) => sum + (h.grade as number), 0) / withGrade.length : 0;
    const avgFVNum =
      withFV.length > 0 ? withFV.reduce((sum, h) => sum + (h.fairValueScore as number), 0) / withFV.length : 0;
    const avgLiqNum =
      withLiq.length > 0 ? withLiq.reduce((sum, h) => sum + (h.liquidityScore as number), 0) / withLiq.length : 0;

    const forSaleCount = holdings.filter((h) => (h.qtyForSale || 0) > 0).length;
    const openBids = holdings.filter((h) => (h.bidsCount || 0) > 0).length;

    const health: HealthStat[] = [
      {
        label: 'Avg Grade (0-10)',
        value: withGrade.length > 0 ? avgGradeNum.toFixed(1) : '-',
        numericValue: avgGradeNum,
        max: 10,
        min: 0,
        iconBg: '#A1A3F9',
        iconBgDark: '#7B7DE0',
        valueColor: '#6B5CE7',
        valueColorDark: '#A1A3F9',
        barGradient: 'linear-gradient(90deg, #6B5CE7, #A1A3F9)',
        barGradientDark: 'linear-gradient(90deg, #7B7DE0, #CACBFB)',
        tooltip:
          'Overall quality score combining critic ratings, vintage quality, and investment potential. Higher is better.',
        icon: (
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7" stroke="white" strokeWidth="1.5" />
            <circle cx="10" cy="10" r="3" stroke="white" strokeWidth="1.5" />
          </svg>
        ),
      },
      {
        label: 'Fair Value (80-100)',
        value: withFV.length > 0 ? avgFVNum.toFixed(0) : '-',
        numericValue: avgFVNum,
        max: 100,
        min: 80,
        iconBg: '#65B45C',
        iconBgDark: '#4A9E42',
        valueColor: '#2D8B27',
        valueColorDark: '#65B45C',
        barGradient: 'linear-gradient(90deg, #2D8B27, #65B45C)',
        barGradientDark: 'linear-gradient(90deg, #4A9E42, #8FD988)',
        tooltip:
          'How fairly priced your holdings are relative to expected value based on age and quality. 100 = undervalued, 90 = fair, 80 = overvalued.',
        icon: (
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 3L12.5 8H17L13.5 11.5L15 17L10 13.5L5 17L6.5 11.5L3 8H7.5L10 3Z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        label: 'Liquidity (80-100)',
        value: withLiq.length > 0 ? avgLiqNum.toFixed(1) : '-',
        numericValue: avgLiqNum,
        max: 100,
        min: 80,
        iconBg: '#55A2A7',
        iconBgDark: '#438589',
        valueColor: '#3A8A8E',
        valueColorDark: '#5eead4',
        barGradient: 'linear-gradient(90deg, #3A8A8E, #55A2A7)',
        barGradientDark: 'linear-gradient(90deg, #438589, #5eead4)',
        tooltip:
          'How easily your holdings can be sold on the secondary market. Higher scores mean faster, more reliable exits.',
        icon: (
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 2C10 2 4 8.5 4 12.5C4 15.81 6.69 18 10 18C13.31 18 16 15.81 16 12.5C16 8.5 10 2 10 2Z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
    ];

    const trading: TradingStat[] = [
      {
        label: 'For Sale',
        value: `${forSaleCount}`,
        valueColor: '#3A8A8E',
        valueColorDark: '#5eead4',
        ctaText: 'View listings \u2192',
        ctaColor: '#3A8A8E',
        ctaColorDark: '#5eead4',
        onClick: () =>
          navigate(NavigationPath.TRADE, {
            state: { initSubTab: SUB_TABS_ENUMS.MY_OFFERS },
          }),
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M3 6H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path
              d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        label: 'Open Bids',
        value: `${openBids}`,
        valueColor: '#D4903A',
        valueColorDark: '#FBBF24',
        ctaText: 'See all bids \u2192',
        ctaColor: '#D4903A',
        ctaColorDark: '#FBBF24',
        onClick: () =>
          navigate(NavigationPath.MY_PORTFOLIO, {
            state: { defaultSort: 'highestBidPrice', isAsc: false },
          }),
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="7" cy="7" r="1.5" fill="currentColor" />
          </svg>
        ),
      },
    ];

    return { healthStats: health, tradingStats: trading };
  }, [holdings, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 h-full">
        <div>
          <div
            className="h-3 w-28 rounded mb-3 animate-pulse"
            style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }}
          />
          <div className="grid grid-cols-3 gap-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="rounded-xl p-4 animate-pulse"
                style={{
                  backgroundColor: isDark ? '#0F1E32' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#1E3A50' : '#E2E8F0'}`,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full" style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }} />
                  <div className="h-6 w-12 rounded" style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }} />
                </div>
                <div
                  className="h-2 w-full rounded-full mb-2"
                  style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }}
                />
                <div className="h-3 w-20 rounded" style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div
            className="h-3 w-28 rounded mb-3 animate-pulse"
            style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }}
          />
          <div className="grid grid-cols-2 gap-3">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="rounded-xl p-5 animate-pulse"
                style={{
                  backgroundColor: isDark ? '#0F1E32' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#1E3A50' : '#E2E8F0'}`,
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full" style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }} />
                  <div className="h-8 w-14 rounded" style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }} />
                </div>
                <div className="h-4 w-16 rounded mb-1" style={{ backgroundColor: isDark ? '#1E3A50' : '#E2E8F0' }} />
                <div className="h-3 w-24 rounded" style={{ backgroundColor: isDark ? '#1E3A50' : '#F1F5F9' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!holdings.length) return null;

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Portfolio Health */}
      <div>
        <h3
          className="text-[11px] font-semibold tracking-widest uppercase mb-3"
          style={{ color: isDark ? '#64748B' : '#94A3B8' }}
        >
          Portfolio Health
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {healthStats.map((stat) => {
            const range = stat.max - stat.min;
            const pct =
              stat.value !== '-' ? Math.min(100, Math.max(0, ((stat.numericValue - stat.min) / range) * 100)) : 0;
            return <HealthStatCard key={stat.label} stat={stat} pct={pct} isDark={isDark} />;
          })}
        </div>
      </div>

      {/* Trading Activity */}
      <div className="flex-1">
        <h3
          className="text-[11px] font-semibold tracking-widest uppercase mb-3"
          style={{ color: isDark ? '#64748B' : '#94A3B8' }}
        >
          Trading Activity
        </h3>
        <div className="grid grid-cols-2 gap-3 h-[calc(100%-28px)]">
          {tradingStats.map((stat) => (
            <button
              key={stat.label}
              className="rounded-xl p-5 transition-all flex flex-col justify-between text-left cursor-pointer hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#0A1628' : '#FFFFFF',
                border: `1px solid ${isDark ? '#1E3A50' : '#E2E8F0'}`,
              }}
              onClick={stat.onClick}
            >
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div style={{ color: isDark ? stat.valueColorDark : stat.valueColor }}>{stat.icon}</div>
                  <span
                    className="text-base sm:text-lg font-bold"
                    style={{ color: isDark ? stat.valueColorDark : stat.valueColor }}
                  >
                    {stat.value}
                  </span>
                </div>
                <div className="text-sm font-medium" style={{ color: isDark ? '#E2E8F0' : '#1E293B' }}>
                  {stat.label}
                </div>
              </div>
              <span
                className="text-xs font-medium mt-3 hover:underline text-left"
                style={{ color: isDark ? stat.ctaColorDark : stat.ctaColor }}
              >
                {stat.ctaText}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioQuickStats;
